import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { useLang, Hreflang } from "@/i18n/LanguageContext";
import { blogPath } from "@/i18n/slugs";
import { NewsletterBox } from "@/components/NewsletterBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catColors = {
  prix: "bg-blue-100 text-blue-700",
  primes: "bg-green-100 text-green-700",
  conseils: "bg-orange-100 text-orange-700",
};

export default function BlogPage() {
  const { lang, T } = useLang();
  const B = T.blog;
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((res) => setPosts(res.data)).catch(() => setPosts([]));
  }, []);

  const canonical = `https://devismaison.be${blogPath(lang)}`;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <Helmet>
        <title>{B.metaTitle}</title>
        <meta name="description" content={B.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={B.metaTitle} />
        <meta property="og:description" content={B.metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : "fr_BE"} />
      </Helmet>
      <Hreflang frPath="/blog" nlPath="/nl/blog" />
      <Navbar />

      <section className="pt-24 pb-12 lg:pt-32 hero-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-4">
            {B.title}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">{B.subtitle}</p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts === null ? (
            <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-slate-500 py-20">{B.empty}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => {
                const slug = lang === "nl" ? post.slug_nl : post.slug;
                const title = lang === "nl" ? post.title_nl : post.title_fr;
                const excerpt = lang === "nl" ? post.excerpt_nl : post.excerpt_fr;
                return (
                  <Link
                    key={post.id}
                    to={`${blogPath(lang)}/${slug}`}
                    className="group category-card flex flex-col"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    {post.image_url && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={post.image_url} alt={title} className="category-card-image" loading="lazy" />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${catColors[post.category] || catColors.conseils}`}>
                          {B.categories[post.category] || post.category}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(post.created_at).toLocaleDateString(lang === "nl" ? "nl-BE" : "fr-BE", { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                      </div>
                      <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-orange-500 transition-colors">
                        {title}
                      </h2>
                      <p className="text-sm text-slate-600 mb-4 flex-1">{excerpt}</p>
                      <div className="flex items-center gap-2 text-orange-500 font-medium text-sm">
                        <span>{B.readMore}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          <div className="max-w-2xl mx-auto mt-16">
            <NewsletterBox />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
